/*
  Lichcards — toont bij losse kaarten de werkelijke uitvoering in plaats van
  de Cardmarket-variantwaarde ("Normal" op een holo Rare uit een SV-set).

  De vertaaltabel wordt server-side meegegeven in data-map:
      { "Normal": { "l": "Holo", "n": "Glanzende folielaag..." }, ... }

  Alleen de ZICHTBARE tekst wordt vervangen. De input-waarden, de optiewaarden
  van het <select>-element en het formulier blijven ongemoeid, zodat de
  variantkeuze en de koopknop van Ella exact blijven werken.

  Staat een waarde niet in de tabel, dan blijft hij onveranderd staan.

  ---------------------------------------------------------------------------
  FIX 18 aug 2026 — vastlopende tab bij het wisselen van uitvoering.

  Ella schrijft bij het aanklikken van een pill zelf de RUWE waarde terug in
  [data-selected-value] (product-info.js, updateSelectionMetadata:
  `selectedSwatchValue.innerHTML = value`). Daar reageren we op met een
  MutationObserver, zodat er geen "Normal" opflitst tot de sectie-fetch klaar is.

  Die observer mag zijn eigen schrijfactie nooit terugkrijgen. In de eerste
  versie gebeurde dat wel:
    - een characterData-mutatie wordt door de browser OOK gemeld als de tekst
      identiek blijft, en
    - observer-callbacks lopen als microtask, dus een vlaggetje dat in dezelfde
      tick weer op false gaat is al lang vrijgegeven als de callback vuurt.
  Resultaat: een microtaskwachtrij die nooit leegloopt — geen rendering, geen
  events, tab bevroren.

  Drie onafhankelijke sloten nu:
    1. schrijven alleen als de tekst echt verandert;
    2. observer losknopen vóór het schrijven en pas erna weer aanhaken
       (disconnect() leegt de wachtrij, dus eigen mutaties komen niet terug);
    3. noodrem: onwaarschijnlijk vaak vuren binnen één seconde -> observer
       definitief uit. Liever een label dat niet meesynchroniseert dan een
       winkel die vastloopt.
  ---------------------------------------------------------------------------
*/
(function () {
  'use strict';

  var OBS_OPTS = { childList: true, characterData: true, subtree: true };

  /* Noodrem: meer dan dit aantal callbacks binnen één seconde kan geen
     echte klantinteractie zijn. */
  var MAX_RUNS_PER_SECOND = 40;

  function esc(id) {
    if (window.CSS && CSS.escape) return CSS.escape(id);
    return id.replace(/([^\w-])/g, '\\$1');
  }

  function now() {
    return window.performance && performance.now ? performance.now() : +new Date();
  }

  /* Vervangt alleen het eerste tekstknooppunt, zodat een eventuele
     "uitverkocht"-span in hetzelfde label blijft staan.
     Schrijft niets als de tekst al klopt — slot 1. */
  function setText(el, text) {
    if (!el) return false;
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim() !== '') {
        if (n.nodeValue === text) return false;
        n.nodeValue = text;
        return true;
      }
    }
    el.insertBefore(document.createTextNode(text), el.firstChild);
    return true;
  }

  class LcFinish extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.map = JSON.parse(this.getAttribute('data-map') || '{}');
      } catch (e) {
        this.map = {};
      }

      /* Blijf binnen de eigen optie. [data-selected-value] wordt elders in het
         thema ook gebruikt (o.a. de conditieregel in lichcards-product-meta),
         en dat veld mag deze code nooit te pakken krijgen. */
      this.scope =
        this.closest('.variant-option') ||
        this.closest('.product-form__input') ||
        this.parentNode;
      if (!this.scope || !this.scope.querySelector) return;

      this.noteEl = this.querySelector('[data-lc-fin-note]');
      this.editionEl = this.querySelector('[data-lc-fin-edition]');
      this.editionValue = this.querySelector('[data-lc-fin-edition-value]');
      this.editionNote = this.querySelector('[data-lc-fin-edition-note]');
      this.holder = this.findHolder();

      this.run();
      this.watch();
    }

    findHolder() {
      var el = this.scope.querySelector('[data-selected-value]');
      if (!el) return null;
      if (el.closest && el.closest('[data-lc-product-meta]')) return null;
      return el;
    }

    labelFor(raw) {
      var e = this.map[raw];
      return e && e.l ? e.l : null;
    }

    noteFor(raw) {
      var e = this.map[raw];
      if (e && typeof e.fn === 'string') return e.fn;
      return e && e.n ? e.n : '';
    }

    selectedLabelFor(raw) {
      var e = this.map[raw];
      return e && e.f ? e.f : this.labelFor(raw);
    }

    /* Pill-labels en dropdown-opties van hun echte uitvoering voorzien.
       Sinds de server-side variant (product-variant-options.liquid) staat het
       juiste label er meestal al; dit vangt de plekken op die nog langs de
       oude weg renderen. */
    relabel() {
      var self = this;

      this.scope.querySelectorAll('input[type="radio"]').forEach(function (input) {
        var lab = self.scope.querySelector('label[for="' + esc(input.id) + '"]');
        if (!lab || lab.dataset.lcFinDone) return;
        var mapped = self.labelFor(input.value);
        if (!mapped) return;
        var tip = lab.querySelector('.tooltip, .sbs-text');
        setText(tip || lab, mapped);
        lab.dataset.lcFinDone = '1';
        /* De oorspronkelijke Cardmarket-waarde blijft opvraagbaar. */
        if (lab.getAttribute('title') !== input.value) {
          lab.setAttribute('title', input.value);
        }
      });

      this.scope.querySelectorAll('option').forEach(function (opt) {
        if (opt.dataset.lcFinDone) return;
        var mapped = self.labelFor(opt.value);
        if (!mapped) return;
        if (opt.textContent !== mapped) opt.textContent = mapped;
        opt.dataset.lcFinDone = '1';
      });
    }

    /* Ella schrijft de ruwe waarde in [data-selected-value]; die vertalen we
       terug zodra hij verandert. */
    syncNote() {
      var holder = this.holder;
      var raw = holder
        ? (holder.getAttribute('data-lc-fin-raw') || holder.textContent || '').trim()
        : '';

      var checked = this.scope.querySelector('input[type="radio"]:checked');
      var sel = this.scope.querySelector('select');
      if (checked) raw = checked.value;
      else if (sel) raw = sel.value;

      var mapped = this.selectedLabelFor(raw);
      if (holder && mapped) {
        if (holder.getAttribute('data-lc-fin-raw') !== raw) {
          holder.setAttribute('data-lc-fin-raw', raw);
        }
        setText(holder, mapped);
      }
      if (this.noteEl) {
        var note = this.noteFor(raw);
        if (this.noteEl.textContent !== note) this.noteEl.textContent = note;
      }
      if (this.editionEl) {
        var entry = this.map[raw];
        var edition = entry && entry.e ? entry.e : '';
        var editionNote = entry && entry.en ? entry.en : '';
        if (this.editionValue && this.editionValue.textContent !== edition) {
          this.editionValue.textContent = edition;
        }
        if (this.editionNote && this.editionNote.textContent !== editionNote) {
          this.editionNote.textContent = editionNote;
        }
        this.editionEl.hidden = !edition;
      }
    }

    /* Slot 2: nooit schrijven terwijl de observer luistert. */
    run() {
      this.pause();
      try {
        this.relabel();
        this.syncNote();
      } finally {
        this.resume();
      }
    }

    pause() {
      if (this._obs) this._obs.disconnect();
    }

    resume() {
      if (this._obs && this.holder && this._alive !== false) {
        this._obs.observe(this.holder, OBS_OPTS);
      }
    }

    /* Slot 3: noodrem. */
    tripped() {
      var t = now();
      if (!this._winStart || t - this._winStart > 1000) {
        this._winStart = t;
        this._runs = 0;
      }
      this._runs++;
      if (this._runs <= MAX_RUNS_PER_SECOND) return false;
      this._alive = false;
      if (this._obs) this._obs.disconnect();
      if (window.console && console.warn) {
        console.warn('[lichcards-finish] observer gestopt: te veel mutaties.');
      }
      return true;
    }

    watch() {
      var self = this;

      this.scope.addEventListener('change', function () {
        if (self._alive === false) return;
        self.run();
      });

      if (this.holder && window.MutationObserver) {
        this._obs = new MutationObserver(function () {
          if (self._alive === false) return;
          if (self.tripped()) return;
          self.run();
        });
        this._obs.observe(this.holder, OBS_OPTS);
      }
    }

    disconnectedCallback() {
      this._alive = false;
      if (this._obs) this._obs.disconnect();
    }
  }

  if (!customElements.get('lc-finish')) {
    customElements.define('lc-finish', LcFinish);
  }
})();
